// src/api/supabase.js

import { createClient } from '@supabase/supabase-js';

// ⚠️ Security Reminder: Use environment variables for these keys in production.
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://vlakljvouimjemxbcnmo.supabase.co';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZsYWtsanZvdWltamVteGJjbm1vIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MjY2MTIxMTAsImV4cCI6MjA0MjE4ODExMH0.eDEDBU8MJraE3XkpcbvviZQEfUD06xq4qVF0KW1kskA';

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

/**
 * -------------- Connection and Table Checks --------------
 */

// Enhanced Connection Test
export const testSupabaseConnection = async () => {
  try {
    // Test general connection by fetching one record from 'time_off_requests'
    console.log('Testing general connection...');
    const { data: tables, error: tablesError } = await supabase
      .from('time_off_requests')
      .select('id')
      .limit(1);

    if (tablesError) {
      console.error('Error accessing time_off_requests:', tablesError);
      throw tablesError;
    }
    console.log('Tables accessible:', tables);

    // Test join query by fetching related employee data
    console.log('Testing join query...');
    const { data: joinData, error: joinError } = await supabase
      .from('time_off_requests')
      .select(`
        id,
        employee:employees (
          id,
          name
        )
      `)
      .limit(1);

    if (joinError) {
      console.error('Error with join query:', joinError);
      throw joinError;
    }
    console.log('Join query result:', joinData);

    return true;
  } catch (error) {
    console.error('Supabase connection/query failed:', error);
    return false;
  }
};

// Utility Function to Check Tables
export const checkTables = async () => {
  const tables = ['time_off_requests', 'employees', 'daily_planning', 'plannings', 'off_dates'];
  for (const table of tables) {
    try {
      console.log(`Checking table: ${table}`);
      const { data, error } = await supabase
        .from(table)
        .select('id')
        .limit(1);
      if (error) {
        console.error(`Error checking table ${table}:`, error);
      } else {
        console.log(`Table ${table} is accessible.`);
      }
    } catch (err) {
      console.error(`Error checking table ${table}:`, err);
    }
  }
};

/**
 * -------------- Employees Management --------------
 */

// Fetch All Employees
export const getEmployees = async () => {
  const { data, error } = await supabase
    .from('employees')
    .select('*')
    .order('name', { ascending: true }); // Ordering by name for better UX

  return { data, error };
};

// Fetch All Employees with Last Off Date
export const getEmployeesWithLastOffDate = async () => {
  const { data, error } = await supabase
    .from('employees')
    .select(`
      *,
      off_dates (
        off_date
      )
    `)
    .order('name', { ascending: true });

  if (error) return { data: null, error };

  // Compute the last off date for each employee
  const employeesWithLastOffDate = data.map(employee => {
    const sortedOffDates = employee.off_dates.sort((a, b) => new Date(b.off_date) - new Date(a.off_date));
    return {
      ...employee,
      last_off_date: sortedOffDates.length > 0 ? sortedOffDates[0].off_date : null,
    };
  });

  return { data: employeesWithLastOffDate, error };
};

// Update an Employee
export const updateEmployee = async (id, updatedFields) => {
  const { data, error } = await supabase
    .from('employees')
    .update(updatedFields)
    .eq('id', id)
    .select(); // Returns the updated row

  return { data, error };
};

// Add a New Employee
export const addEmployee = async (newEmployee) => {
  const { data, error } = await supabase
    .from('employees')
    .insert([newEmployee])
    .select(); // Returns the inserted row

  return { data, error };
};

// Delete an Employee
export const deleteEmployee = async (id) => {
  const { data, error } = await supabase
    .from('employees')
    .delete()
    .eq('id', id);

  return { data, error };
};

// Fetch Active Employees Only
export const getActiveEmployees = async () => {
  try {
    const { data, error } = await supabase
      .from('employees')
      .select('*')
      .eq('status', 'active')
      .order('name');

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching active employees:', error);
    throw error;
  }
};

/**
 * -------------- Off Dates Management --------------
 */

// Fetch Off Dates for an Employee
export const getOffDatesByEmployee = async (employeeId) => {
  const { data, error } = await supabase
    .from('off_dates')
    .select('*')
    .eq('employee_id', employeeId)
    .order('off_date', { ascending: false });

  return { data, error };
};

// Add an Off Date for an Employee
export const addOffDate = async (employeeId, offDate, category = 'OFF') => {
  const { data, error } = await supabase
    .from('off_dates')
    .insert([{ employee_id: employeeId, off_date: offDate, category }])
    .select(); // Returns the inserted row

  return { data, error };
};

// Remove an Off Date
export const removeOffDate = async (offDateId) => {
  const { data, error } = await supabase
    .from('off_dates')
    .delete()
    .eq('id', offDateId);

  return { data, error };
};

// Get All Off Dates with Employee Information
export const getAllOffDates = async () => {
  const { data, error } = await supabase
    .from('off_dates')
    .select(`
      id,
      off_date,
      category,
      employee_id,
      employee:employees (
        id,
        name,
        role
      )
    `)
    .order('off_date', { ascending: false });

  return { data, error };
};

// Get Off Dates by Date Range
export const getOffDatesByRange = async (startDate, endDate) => {
  const { data, error } = await supabase
    .from('off_dates')
    .select(`
      id,
      off_date,
      category,
      employee:employees (
        id,
        name,
        role
      )
    `)
    .gte('off_date', startDate)
    .lte('off_date', endDate)
    .order('off_date', { ascending: true });

  return { data, error };
};

// Add Multiple Off Dates (Bulk Insert/Upsert)
export const addMultipleOffDates = async (offDates) => {
  const { data, error } = await supabase
    .from('off_dates')
    .upsert(offDates, { onConflict: 'employee_id,off_date' })
    .select();

  return { data, error };
};

// Update a single off_date entry
export const updateOffDate = async (offDateId, updates) => {
  const { data, error } = await supabase
    .from('off_dates')
    .update(updates)
    .eq('id', offDateId)
    .select(`
      id,
      off_date,
      category,
      employee:employees (
        id,
        name,
        role
      )
    `);

  return { data, error };
};

// Get off_dates statistics by category for a date range
export const getOffDatesStats = async (startDate, endDate) => {
  const { data, error } = await supabase
    .from('off_dates')
    .select('category')
    .gte('off_date', startDate)
    .lte('off_date', endDate);

  if (error) return { data: null, error };

  // Count by category
  const stats = {
    ASTREINTE: 0,
    BASE: 0,
    SEABOB: 0,
    JETSKI: 0,
    BUREAU: 0,
    ST_KITTS: 0,
    OFF: 0,
    total: data.length
  };

  data.forEach(item => {
    if (stats.hasOwnProperty(item.category)) {
      stats[item.category]++;
    }
  });

  return { data: stats, error: null };
};

// Get off_dates grouped by date
export const getOffDatesGroupedByDate = async (startDate, endDate) => {
  const { data, error } = await supabase
    .from('off_dates')
    .select(`
      id,
      off_date,
      category,
      employee:employees (
        id,
        name,
        role
      )
    `)
    .gte('off_date', startDate)
    .lte('off_date', endDate)
    .order('off_date', { ascending: true });

  if (error) return { data: null, error };

  // Group by date
  const grouped = data.reduce((acc, item) => {
    const date = item.off_date;
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(item);
    return acc;
  }, {});

  return { data: grouped, error: null };
};

// Update Employee Categories from Planning
// This function replaces all categories for a specific date based on planning data
export const updateEmployeeCategoriesFromPlanning = async (planningDate, categorizedEmployees) => {
  try {
    // Only store trackable categories (OFF, ASTREINTE, BASE variants)
    // AT_SEA is the default working state and doesn't need to be stored
    const storedCategories = ['OFF', 'ASTREINTE', 'BASE', 'BASE_AM', 'BASE_PM'];
    const filteredEmployees = categorizedEmployees.filter(emp =>
      storedCategories.includes(emp.category)
    );

    // If no trackable entries, just delete existing and return
    if (filteredEmployees.length === 0) {
      const { error: deleteError } = await supabase
        .from('off_dates')
        .delete()
        .eq('off_date', planningDate);

      if (deleteError) {
        console.error('Error deleting existing off_dates:', deleteError);
        return { data: null, error: deleteError };
      }
      return { data: [], error: null };
    }

    // Step 1: Delete existing entries for this date
    const { error: deleteError } = await supabase
      .from('off_dates')
      .delete()
      .eq('off_date', planningDate);

    if (deleteError) {
      console.error('Error deleting existing off_dates:', deleteError);
      return { data: null, error: deleteError };
    }

    // Step 2: Prepare new entries (only include employee_id, off_date, and category)
    const newEntries = filteredEmployees.map(emp => ({
      employee_id: emp.employee_id,
      off_date: emp.off_date,
      category: emp.category
    }));

    // Step 3: Bulk insert new entries
    const { data, error: insertError } = await supabase
      .from('off_dates')
      .insert(newEntries)
      .select();

    if (insertError) {
      console.error('Error inserting new off_dates:', insertError);
      return { data: null, error: insertError };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error updating employee categories from planning:', error);
    return { data: null, error };
  }
};

/**
 * -------------- Plannings Management --------------
 */

// Fetch Plannings for a Specific Date
export const getPlanningsByDate = async (date) => {
  try {
    const { data, error } = await supabase
      .from('plannings') // Ensure correct table name
      .select(`
        id,
        date,
        shift,
        employee:employees(id, name, role)
      `)
      .eq('date', date)
      .order('id', { ascending: true });

    if (error) throw error;
    console.log('Plannings Data:', data);
    console.log('Plannings Error:', error);

    return { data, error };
  } catch (error) {
    console.error('Error fetching plannings by date:', error);
    return { data: null, error };
  }
};

// Create a New Planning
export const createPlanning = async (planning) => {
  const { data, error } = await supabase
    .from('plannings')
    .insert([planning])
    .select(); // Returns the inserted row

  return { data, error };
};

// Update an Existing Planning
export const updatePlanning = async (id, updatedFields) => {
  const { data, error } = await supabase
    .from('plannings')
    .update(updatedFields)
    .eq('id', id)
    .select(); // Returns the updated row

  return { data, error };
};

// Delete a Planning
export const deletePlanning = async (id) => {
  const { data, error } = await supabase
    .from('plannings')
    .delete()
    .eq('id', id);

  return { data, error };
};

/**
 * -------------- Daily Planning Helpers --------------
 */

// Helper Function to Save Daily Planning
export const saveDailyPlanning = async (date, employeeIds) => {
  try {
    // First, delete existing plannings for the date
    const { error: deleteError } = await supabase
      .from('plannings')
      .delete()
      .eq('date', date);

    if (deleteError) throw deleteError;

    // Then, insert new plannings
    if (employeeIds.length > 0) {
      const { error: insertError } = await supabase
        .from('plannings')
        .insert(
          employeeIds.map(employeeId => ({
            employee: employeeId, // UUID string
            date,
            shift: 'full_day' // Default shift type, modify as needed
          }))
        )
        .select();

      if (insertError) throw insertError;
    }

    return { error: null };
  } catch (error) {
    console.error('Error saving daily planning:', error);
    return { error };
  }
};



// Helper Function to Get Historical Planning
export const getHistoricalPlanning = async (startDate, endDate) => {
  try {
    const { data, error } = await supabase
      .from('plannings')
      .select(`
        id,
        date,
        shift,
        employee:employees(id, name, role)
      `)
      .gte('date', startDate)
      .lte('date', endDate)
      .order('date', { ascending: false });

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error fetching historical planning:', error);
    return { data: null, error };
  }
};

/**
 * -------------- Storage Management (PDF Templates) --------------
 */

// Upload PDF Template to Storage
export const uploadPDFTemplate = async (file, filename) => {
  try {
    const { data, error } = await supabase.storage
      .from('templates')
      .upload(filename, file, {
        cacheControl: '3600',
        upsert: true, // Replace existing file with same name
        contentType: 'application/pdf'
      });

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error uploading PDF template:', error);
    return { data: null, error };
  }
};

// Get List of PDF Templates
export const getPDFTemplates = async () => {
  try {
    const { data, error } = await supabase.storage
      .from('templates')
      .list('', {
        limit: 100,
        offset: 0,
        sortBy: { column: 'name', order: 'asc' }
      });

    if (error) throw error;

    // Filter only PDF files
    const pdfFiles = data.filter(file => file.name.toLowerCase().endsWith('.pdf'));
    return { data: pdfFiles, error: null };
  } catch (error) {
    console.error('Error fetching PDF templates:', error);
    return { data: null, error };
  }
};

// Download PDF Template as Blob
export const downloadPDFTemplate = async (filename) => {
  try {
    const { data, error } = await supabase.storage
      .from('templates')
      .download(filename);

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error downloading PDF template:', error);
    return { data: null, error };
  }
};

// Get Public URL for PDF Template
export const getPDFTemplateUrl = (filename) => {
  const { data } = supabase.storage
    .from('templates')
    .getPublicUrl(filename);

  return data.publicUrl;
};

// Delete PDF Template
export const deletePDFTemplate = async (filename) => {
  try {
    const { data, error } = await supabase.storage
      .from('templates')
      .remove([filename]);

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error deleting PDF template:', error);
    return { data: null, error };
  }
};

/**
 * -------------- Contract Payments Management --------------
 */

// Get all contract payments
export const getContractPayments = async () => {
  const { data, error } = await supabase
    .from('contract_payments')
    .select('*')
    .order('created_at', { ascending: false });

  return { data, error };
};

// Get payments by charter ID
export const getPaymentsByCharterId = async (charterId) => {
  const { data, error } = await supabase
    .from('contract_payments')
    .select('*')
    .eq('charter_id', charterId)
    .order('created_at', { ascending: false });

  return { data, error };
};

// Create a new payment record
export const createContractPayment = async (paymentData) => {
  const { data, error } = await supabase
    .from('contract_payments')
    .insert([{
      ...paymentData,
      created_at: new Date().toISOString()
    }])
    .select();

  return { data, error };
};

// Update payment status
export const updatePaymentStatus = async (paymentId, status, additionalData = {}) => {
  const updateData = {
    status,
    ...additionalData
  };

  if (status === 'paid') {
    updateData.paid_at = new Date().toISOString();
  }

  const { data, error } = await supabase
    .from('contract_payments')
    .update(updateData)
    .eq('id', paymentId)
    .select();

  return { data, error };
};

// Update payment by charter ID
export const updatePaymentByCharterId = async (charterId, updateData) => {
  const { data, error } = await supabase
    .from('contract_payments')
    .update(updateData)
    .eq('charter_id', charterId)
    .select();

  return { data, error };
};

// Delete a payment record
export const deleteContractPayment = async (paymentId) => {
  const { data, error } = await supabase
    .from('contract_payments')
    .delete()
    .eq('id', paymentId);

  return { data, error };
};

// Get payment statistics
export const getPaymentStats = async () => {
  const { data, error } = await supabase
    .from('contract_payments')
    .select('status, amount');

  if (error) return { data: null, error };

  const stats = {
    total: data.length,
    pending: 0,
    link_sent: 0,
    paid: 0,
    failed: 0,
    total_amount: 0,
    paid_amount: 0
  };

  data.forEach(payment => {
    stats[payment.status] = (stats[payment.status] || 0) + 1;
    stats.total_amount += payment.amount || 0;
    if (payment.status === 'paid') {
      stats.paid_amount += payment.amount || 0;
    }
  });

  return { data: stats, error: null };
};

/**
 * -------------- Contract Workflows (Signature + Payment) --------------
 */

// Generate a unique link
const generateUniqueLink = () => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Create a new contract workflow
export const createContractWorkflow = async (workflowData) => {
  const uniqueLink = generateUniqueLink();

  const { data, error } = await supabase
    .from('contract_workflows')
    .insert([{
      ...workflowData,
      unique_link: uniqueLink,
      status: 'draft',
      created_at: new Date().toISOString()
    }])
    .select();

  return { data, error, uniqueLink };
};

// Get workflow by unique link
export const getContractWorkflowByLink = async (uniqueLink) => {
  const { data, error } = await supabase
    .from('contract_workflows')
    .select('*')
    .eq('unique_link', uniqueLink)
    .single();

  return { data, error };
};

// Get all contract workflows
export const getContractWorkflows = async () => {
  const { data, error } = await supabase
    .from('contract_workflows')
    .select('*')
    .order('created_at', { ascending: false });

  return { data, error };
};

// Update contract workflow
export const updateContractWorkflow = async (id, updateData) => {
  const { data, error } = await supabase
    .from('contract_workflows')
    .update(updateData)
    .eq('id', id)
    .select();

  return { data, error };
};

// Update workflow by unique link
export const updateContractWorkflowByLink = async (uniqueLink, updateData) => {
  const { data, error } = await supabase
    .from('contract_workflows')
    .update(updateData)
    .eq('unique_link', uniqueLink)
    .select();

  return { data, error };
};

// Delete contract workflow
export const deleteContractWorkflow = async (id) => {
  const { data, error } = await supabase
    .from('contract_workflows')
    .delete()
    .eq('id', id);

  return { data, error };
};

// Get workflow statistics
export const getContractWorkflowStats = async () => {
  const { data, error } = await supabase
    .from('contract_workflows')
    .select('status');

  if (error) return { data: null, error };

  const stats = {
    total: data.length,
    draft: 0,
    signature_sent: 0,
    signed: 0,
    payment_sent: 0,
    paid: 0,
    completed: 0
  };

  data.forEach(workflow => {
    if (stats.hasOwnProperty(workflow.status)) {
      stats[workflow.status]++;
    }
  });

  return { data: stats, error: null };
};

/**
 * -------------- Knowledge Base Management --------------
 */

// Get all knowledge articles
export const getKnowledgeArticles = async () => {
  const { data, error } = await supabase
    .from('knowledge_articles')
    .select('*')
    .order('updated_at', { ascending: false });

  return { data, error };
};

// Get a single knowledge article by ID
export const getKnowledgeArticle = async (id) => {
  const { data, error } = await supabase
    .from('knowledge_articles')
    .select('*')
    .eq('id', id)
    .single();

  return { data, error };
};

// Create a new knowledge article
export const createKnowledgeArticle = async (article) => {
  const { data, error } = await supabase
    .from('knowledge_articles')
    .insert([{
      ...article,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }])
    .select();

  return { data, error };
};

// Update a knowledge article
export const updateKnowledgeArticle = async (id, updates) => {
  const { data, error } = await supabase
    .from('knowledge_articles')
    .update({
      ...updates,
      updated_at: new Date().toISOString()
    })
    .eq('id', id)
    .select();

  return { data, error };
};

// Delete a knowledge article
export const deleteKnowledgeArticle = async (id) => {
  const { data, error } = await supabase
    .from('knowledge_articles')
    .delete()
    .eq('id', id);

  return { data, error };
};

// Upload file to knowledge storage
export const uploadKnowledgeFile = async (file, filename) => {
  try {
    const { data, error } = await supabase.storage
      .from('knowledge')
      .upload(filename, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error uploading knowledge file:', error);
    return { data: null, error };
  }
};

// Get public URL for knowledge file
export const getKnowledgeFileUrl = (filename) => {
  const { data } = supabase.storage
    .from('knowledge')
    .getPublicUrl(filename);

  return data.publicUrl;
};

// Delete knowledge file
export const deleteKnowledgeFile = async (filename) => {
  try {
    const { data, error } = await supabase.storage
      .from('knowledge')
      .remove([filename]);

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error deleting knowledge file:', error);
    return { data: null, error };
  }
};

// List all files in knowledge storage
export const listKnowledgeFiles = async () => {
  try {
    const { data, error } = await supabase.storage
      .from('knowledge')
      .list('', {
        limit: 100,
        offset: 0,
        sortBy: { column: 'name', order: 'asc' }
      });

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error listing knowledge files:', error);
    return { data: null, error };
  }
};
